/**
 * 字幕编辑与翻译结果展示组件
 */

import React, { useState, useEffect } from 'react'
import { transcriptionToSrt, transcriptionToVtt, downloadTextFile, SUPPORTED_LANGUAGES, TTS_VOICES } from '../utils/subtitleUtils'

export default function SubtitleEditor({
  segments,
  translatedTexts,
  onUpdateTranslation,
  targetLanguage,
  onTargetLanguageChange,
  sourceLanguage,
  onSourceLanguageChange,
  ttsVoice,
  onTtsVoiceChange,
  onTranslate,
  translating,
  onExportSrt,
  onExportVtt,
  onSynthesizeAll,
  synthesizing,
}) {
  const [activeTab, setActiveTab] = useState('original')
  const [editedTexts, setEditedTexts] = useState([])

  useEffect(() => {
    if (translatedTexts) {
      setEditedTexts(translatedTexts)
    }
  }, [translatedTexts])

  const handleTextEdit = (index, newText) => {
    const updated = [...editedTexts]
    updated[index] = newText
    setEditedTexts(updated)
    onUpdateTranslation(updated)
  }

  const formatTime = (seconds) => {
    const m = Math.floor(seconds / 60)
    const s = Math.floor(seconds % 60)
    const ms = Math.floor((seconds % 1) * 1000)
    return `${m}:${s.toString().padStart(2, '0')}.${ms.toString().padStart(3, '0')}`
  }

  const originalSrt = segments ? transcriptionToSrt(segments) : ''
  const translatedSegments = segments?.map((seg, i) => ({
    ...seg,
    text: editedTexts[i] || seg.text,
  }))
  const translatedSrt = translatedSegments ? transcriptionToSrt(translatedSegments) : ''

  return (
    <div style={styles.container}>
      <h3 style={styles.title}>📝 字幕编辑与翻译</h3>

      {/* 控制栏 */}
      <div style={styles.controls}>
        <div style={styles.controlGroup}>
          <label style={styles.label}>源语言</label>
          <select
            value={sourceLanguage}
            onChange={(e) => onSourceLanguageChange(e.target.value)}
            style={styles.select}
          >
            <option value="auto">自动检测</option>
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.label}>目标语言</label>
          <select
            value={targetLanguage}
            onChange={(e) => onTargetLanguageChange(e.target.value)}
            style={styles.select}
          >
            {SUPPORTED_LANGUAGES.map((l) => (
              <option key={l.code} value={l.code}>
                {l.name}
              </option>
            ))}
          </select>
        </div>

        <div style={styles.controlGroup}>
          <label style={styles.label}>TTS 音色</label>
          <select
            value={ttsVoice}
            onChange={(e) => onTtsVoiceChange(e.target.value)}
            style={styles.select}
          >
            {TTS_VOICES.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name} - {v.description}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 操作按钮 */}
      <div style={styles.actions}>
        <button
          onClick={onTranslate}
          disabled={translating || !segments}
          style={{
            ...styles.actionBtn,
            ...styles.primaryBtn,
            ...(translating || !segments ? styles.btnDisabled : {}),
          }}
        >
          {translating ? '翻译中...' : '🌐 翻译字幕'}
        </button>

        <button
          onClick={() => downloadTextFile(originalSrt, 'original_subtitles.srt')}
          disabled={!segments}
          style={{
            ...styles.actionBtn,
            ...( !segments ? styles.btnDisabled : {}),
          }}
        >
          📥 下载原文字幕
        </button>

        <button
          onClick={() => downloadTextFile(translatedSrt, `translated_${targetLanguage}.srt`)}
          disabled={!translatedSrt}
          style={{
            ...styles.actionBtn,
            ...(!translatedSrt ? styles.btnDisabled : {}),
          }}
        >
          📥 下载译文字幕
        </button>

        <button
          onClick={onSynthesizeAll}
          disabled={synthesizing || !editedTexts.length}
          style={{
            ...styles.actionBtn,
            ...styles.ttsBtn,
            ...(synthesizing || !editedTexts.length ? styles.btnDisabled : {}),
          }}
        >
          {synthesizing ? '合成中...' : '🔊 合成全部语音'}
        </button>
      </div>

      {/* 标签页 */}
      <div style={styles.tabs}>
        <button
          onClick={() => setActiveTab('original')}
          style={{
            ...styles.tab,
            ...(activeTab === 'original' ? styles.tabActive : {}),
          }}
        >
          原文字幕 ({segments?.length || 0})
        </button>
        <button
          onClick={() => setActiveTab('translated')}
          style={{
            ...styles.tab,
            ...(activeTab === 'translated' ? styles.tabActive : {}),
          }}
        >
          译文字幕 ({editedTexts.length || 0})
        </button>
      </div>

      {/* 字幕列表 */}
      <div style={styles.list}>
        {activeTab === 'original' && segments?.map((seg, i) => (
          <div key={i} style={styles.item}>
            <div style={styles.itemHeader}>
              <span style={styles.itemIndex}>#{i + 1}</span>
              <span style={styles.itemTime}>
                {formatTime(seg.start)} → {formatTime(seg.end)}
              </span>
            </div>
            <div style={styles.itemText}>{seg.text}</div>
          </div>
        ))}

        {activeTab === 'translated' &&
          segments?.map((seg, i) => (
            <div key={i} style={styles.item}>
              <div style={styles.itemHeader}>
                <span style={styles.itemIndex}>#{i + 1}</span>
                <span style={styles.itemTime}>
                  {formatTime(seg.start)} → {formatTime(seg.end)}
                </span>
              </div>
              <textarea
                value={editedTexts[i] || seg.text}
                onChange={(e) => handleTextEdit(i, e.target.value)}
                style={styles.textarea}
                rows={2}
              />
            </div>
          ))}

        {!segments && (
          <div style={styles.empty}>请先上传视频并提取字幕</div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    borderRadius: '16px',
    padding: '24px',
    border: '1px solid rgba(138, 43, 226, 0.3)',
    marginBottom: '20px',
  },
  title: {
    margin: '0 0 16px 0',
    color: '#e2e8f0',
    fontSize: '18px',
  },
  controls: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  controlGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    flex: '1 1 140px',
  },
  label: {
    color: '#94a3b8',
    fontSize: '12px',
    fontWeight: 500,
  },
  select: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    background: 'rgba(15, 23, 42, 0.6)',
    color: '#e2e8f0',
    fontSize: '13px',
    outline: 'none',
  },
  actions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
    marginBottom: '16px',
  },
  actionBtn: {
    padding: '10px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    background: 'rgba(15, 23, 42, 0.5)',
    color: '#e2e8f0',
    fontSize: '13px',
    cursor: 'pointer',
  },
  primaryBtn: {
    background: 'linear-gradient(135deg, #8a2be2, #6b21a8)',
    border: 'none',
    fontWeight: 600,
  },
  ttsBtn: {
    background: 'linear-gradient(135deg, #059669, #047857)',
    border: 'none',
    fontWeight: 600,
  },
  btnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '12px',
    borderBottom: '1px solid rgba(148, 163, 184, 0.1)',
    paddingBottom: '8px',
  },
  tab: {
    padding: '8px 16px',
    borderRadius: '8px',
    border: 'none',
    background: 'transparent',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer',
  },
  tabActive: {
    background: 'rgba(138, 43, 226, 0.2)',
    color: '#e2e8f0',
    fontWeight: 600,
  },
  list: {
    maxHeight: '480px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    padding: '12px',
    background: 'rgba(15, 23, 42, 0.4)',
    borderRadius: '10px',
    border: '1px solid rgba(148, 163, 184, 0.08)',
  },
  itemHeader: {
    display: 'flex',
    gap: '12px',
    marginBottom: '6px',
    alignItems: 'center',
  },
  itemIndex: {
    color: '#8a2be2',
    fontSize: '12px',
    fontWeight: 700,
    minWidth: '36px',
  },
  itemTime: {
    color: '#64748b',
    fontSize: '11px',
    fontFamily: 'monospace',
  },
  itemText: {
    color: '#e2e8f0',
    fontSize: '14px',
    lineHeight: 1.5,
  },
  textarea: {
    width: '100%',
    padding: '8px 10px',
    borderRadius: '6px',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    background: 'rgba(15, 23, 42, 0.6)',
    color: '#e2e8f0',
    fontSize: '14px',
    lineHeight: 1.5,
    resize: 'vertical',
    outline: 'none',
    boxSizing: 'border-box',
  },
  empty: {
    textAlign: 'center',
    color: '#64748b',
    padding: '40px 20px',
    fontSize: '14px',
  },
}